/**
 * §P.7 fase 2 · La marca del club: su color.
 *
 * ## El contraste se **resuelve**, no se rechaza
 *
 * Es la decisión del contrato y merece repetirse aquí, porque es la que hace que
 * esta pantalla no se sienta en contra de quien la usa: «el azul del club es el azul
 * del club, y devolver un `422` porque su marca de quince años no llega a un umbral
 * es la clase de respuesta que hace que un producto se sienta en contra de quien lo
 * usa».
 *
 * Así que aquí **no hay validación de contraste**: se guarda el color que el club
 * quiera. Lo que sí se dice es cuándo su color necesita texto oscuro, porque eso
 * explica por qué los botones cambian de aspecto.
 *
 * ## La muestra calcula en local, y eso no contradice lo anterior
 *
 * `marca.ts` dice que `brand_contrast` lo decide el servidor y que aquí no se
 * recalcula, «la misma trampa que tenían los precios en dos sitios». Sigue valiendo
 * para **lo que se pinta de verdad**: las variables CSS del panel salen de la
 * respuesta del servidor.
 *
 * Pero una muestra de un color **que todavía no se ha guardado** no tiene respuesta
 * del servidor que usar: `brand_contrast` habla del color anterior. La primera versión
 * usaba ese `text_color` y con `brand_color` a `null` pintaba «Guardar el alumno» en
 * blanco sobre amarillo —ilegible, y encima mentira, porque al guardar sale oscuro—.
 * Se vio en el navegador escribiendo un amarillo.
 *
 * Así que la muestra usa `textoSobre`, que es para lo que está: prever. El servidor
 * sigue siendo quien decide, y en cuanto se guarda manda él.
 *
 * ## El color que se guarda es **uno**
 *
 * La escala de doce tonos se deriva al pintar (`brainstorming.md` §7.7). Guardarla
 * sería congelar hoy una decisión de diseño que va a cambiar, y con ella doscientas
 * filas que migrar. Por eso esta pantalla pide un color y no una paleta.
 *
 * ## El logo, y por qué son dos · 22-09-2026
 *
 * Estuvo sin cablear hasta que §S.3 entró en el panel, y se decía en pantalla en vez
 * de enseñar un botón que no hacía nada. Ya está.
 *
 * **Dos logos y no uno**: el normal y el de fondo oscuro. No es un capricho de diseño
 * — un logo negro sobre la cabecera oscura de la app no se ve, y es justo donde más
 * mira una familia. El segundo es opcional: sin él se usa el primero, que es lo que
 * quiere un club con un logo claro.
 *
 * **PNG o SVG**, y el SVG es el bueno: el logo acaba impreso en un diploma, y un PNG
 * de 200 píxeles ampliado a un A4 se ve pixelado en la pared de una habitación
 * infantil.
 *
 * ## El dominio propio se lee y no se toca
 *
 * Es único en toda la plataforma y apunta a una configuración de DNS que hay que
 * verificar: se cambia desde §A. Se enseña porque el club quiere saber si lo tiene.
 * Y solo si lo tiene: todavía no se puede contratar (`planes_premium.md` §6.1).
 */
import { useState } from 'react';
import { SubirFichero } from '@/shared/ui/SubirFichero';
import { useImagen } from '@/shared/api/imagenes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import {
  COLOR_POR_DEFECTO,
  contraste,
  esColorValido,
  textoSobre,
} from '@/shared/ui/marca';
import type { MarcaDelClub } from '@/club/ajustes/tipos';

export function Marca({
  club,
  puedeEditar,
}: {
  club: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [color, setColor] = useState('');
  const [sinMarca, setSinMarca] = useState(false);
  const [sembradoDe, setSembradoDe] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [guardado, setGuardado] = useState(false);

  const marca = useQuery({
    queryKey: ['branding', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).branding.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as MarcaDelClub;
    },
  });

  const logoClaro = useImagen(marca.data?.logo_asset_id);
  const logoOscuro = useImagen(marca.data?.logo_dark_asset_id);

  // Sembrado en el render, no desde un efecto. Mismo patrón que el resto de §P.7.
  if (marca.data && sembradoDe !== club) {
    setSembradoDe(club);
    setColor(marca.data.brand_color ?? '');
    setSinMarca(marca.data.hide_kiap_branding);
  }

  /**
   * Guarda **solo el logo** y no toca el color.
   *
   * Aparte del `guardar` de abajo a propósito: el color y la casilla se guardan
   * cuando la persona pulsa, y un logo se guarda en cuanto se sube. Meterlos en la
   * misma llamada obligaría a pulsar «Guardar» después de subir una imagen, que es
   * el paso que todo el mundo se salta.
   */
  const ponerLogo = useMutation({
    mutationFn: async ({
      claro,
      oscuro,
    }: {
      claro?: string;
      oscuro?: string;
    }) => {
      const r = await api.v1.clubs({ club }).branding.put({
        ...(claro === undefined ? {} : { logo_asset_id: claro }),
        ...(oscuro === undefined ? {} : { logo_dark_asset_id: oscuro }),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as MarcaDelClub;
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['marca', club] });
      void cache.invalidateQueries({ queryKey: ['branding', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const guardar = useMutation({
    mutationFn: async () => {
      const r = await api.v1.clubs({ club }).branding.put({
        // Vacío es «vuelve al de Kiap», que no es lo mismo que no mandar el campo.
        brand_color: color.trim() === '' ? null : color.trim().toLowerCase(),
        hide_kiap_branding: sinMarca,
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as MarcaDelClub;
    },
    onSuccess: () => {
      setGuardado(true);
      /*
       * La cabecera del panel lee esta misma consulta para las variables CSS, así que
       * invalidarla es lo que hace que el color nuevo se vea **sin recargar**. Es la
       * gracia de la pantalla: se elige un color y el panel entero cambia delante.
       */
      void cache.invalidateQueries({ queryKey: ['branding', club] });
    },
    onError: (e) => {
      setGuardado(false);
      setProblema(aProblema(e));
    },
  });

  if (marca.isPending) {
    return <Cargando filas={5} etiqueta="Cargando la marca" />;
  }

  if (marca.isError || !marca.data) {
    return (
      <PanelError
        problema={aProblema(marca.error)}
        reintentar={() => void marca.refetch()}
      />
    );
  }

  const m = marca.data;
  const elegido = color.trim() === '' ? COLOR_POR_DEFECTO : color.trim();
  const valido = esColorValido(elegido);
  /* 4,5:1 es el mínimo de texto en WCAG AA. Ver la cabecera: esto es el preview. */
  const necesitaTextoOscuro = valido && contraste(elegido, '#ffffff') < 4.5;
  const cambiado =
    (color.trim() === '' ? null : color.trim().toLowerCase()) !==
      (m.brand_color?.toLowerCase() ?? null) ||
    sinMarca !== m.hide_kiap_branding;

  const cambiar = (v: string) => {
    setColor(v);
    setGuardado(false);
  };

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        guardar.mutate();
      }}
    >
      <h1>La marca del club</h1>

      <p className="tenue">
        El panel lleva tu color porque quien lo ve por encima del hombro es el
        padre de un alumno. Se guarda <strong>un color</strong>: el resto de los
        tonos salen de él al pintar.
      </p>

      <div className="rejilla">
        <label>
          Tu color
          {/*
            Dos campos para el mismo dato: la rueda del sistema para elegir a ojo y
            el hexadecimal para pegar el de la marca. Un club que viene con su
            manual de identidad tiene el `#` escrito, y hacerle buscarlo en una
            rueda es pedirle que lo aproxime.
          */}
          <span className="color-doble">
            <input
              type="color"
              value={valido ? elegido : COLOR_POR_DEFECTO}
              disabled={!puedeEditar}
              onChange={(e) => cambiar(e.target.value)}
              aria-label="Elegir el color con la rueda"
            />
            <input
              type="text"
              maxLength={7}
              placeholder={COLOR_POR_DEFECTO}
              value={color}
              disabled={!puedeEditar}
              onChange={(e) => cambiar(e.target.value)}
              aria-label="El color en hexadecimal"
            />
          </span>
        </label>
      </div>

      {!valido && color.trim() !== '' && (
        <p className="tenue">
          Eso todavía no es un color. Se escribe con almohadilla y tres o seis
          cifras: <code>#1b5e20</code>.
        </p>
      )}

      {/*
        La prueba de verdad: los botones y las pastillas con el color puesto. Un
        cuadrado de color no dice si el texto encima se va a leer; un botón sí.
      */}
      {valido && (
        <>
          <h2 className="seccion">Cómo va a quedar</h2>
          <div
            className="muestra-marca"
            style={{
              ['--muestra' as string]: elegido,
              /* Local, porque el color todavía no está guardado. Ver la cabecera. */
              ['--muestra-texto' as string]: textoSobre(elegido),
            }}
          >
            <span className="muestra-boton">Guardar el alumno</span>
            <span className="muestra-pastilla">Pagado</span>
            <span className="muestra-punto" aria-hidden="true" />
          </div>

          {/*
            Solo cuando pasa: un club con un color oscuro no necesita enterarse de que
            existe esta regla. Y cuando pasa, la frase explica el porqué en vez de
            regañar — el color no se rechaza.

            Se calcula sobre el color **que se está escribiendo** y no sobre el
            guardado: el aviso tiene que salir mientras se elige, que es cuando sirve
            de algo.
          */}
          {necesitaTextoOscuro && (
            <div className="aviso" role="status">
              <strong>
                Tu color es claro, así que el texto encima va oscuro.
              </strong>{' '}
              No hay nada que cambiar: tu color se queda como es y Kiap ajusta
              el texto para que se lea. Es lo que exige la accesibilidad y lo
              que agradece cualquiera leyendo el panel a pleno sol.
            </div>
          )}
        </>
      )}

      <h2 className="seccion">El logo</h2>

      <p className="tenue">
        Va en la cabecera del panel, en la app de las familias y{' '}
        <strong>en los diplomas</strong>, que acaban en la pared de una
        habitación infantil. Si tienes el SVG, usa ese: un PNG pequeño ampliado
        a un A4 se ve pixelado.
      </p>

      <div className="rejilla">
        <div>
          <p className="etiqueta-suelta">Sobre fondo claro</p>
          {logoClaro ? (
            <div className="vista-previa">
              <img src={logoClaro} alt="El logo sobre fondo claro" />
            </div>
          ) : marca.data?.logo_asset_id ? (
            <p className="tenue">Hay uno puesto.</p>
          ) : (
            <p className="tenue">Todavía no hay ninguno.</p>
          )}
          <SubirFichero
            uso="logo"
            club={club}
            etiqueta={marca.data?.logo_asset_id ? 'Cambiarlo' : 'Subir el logo'}
            deshabilitado={!puedeEditar}
            alSubir={(asset) => ponerLogo.mutate({ claro: asset.id })}
          />
        </div>

        <div>
          {/*
            El segundo es opcional y se dice: sin él se usa el primero, que es lo
            que quiere un club cuyo logo ya se ve sobre oscuro.
          */}
          <p className="etiqueta-suelta">Sobre fondo oscuro (opcional)</p>
          {logoOscuro ? (
            <div className="vista-previa vista-previa-oscura">
              <img src={logoOscuro} alt="El logo sobre fondo oscuro" />
            </div>
          ) : marca.data?.logo_dark_asset_id ? (
            <p className="tenue">Hay uno puesto.</p>
          ) : (
            <p className="tenue">Sin él se usa el de arriba.</p>
          )}
          <SubirFichero
            uso="logo"
            club={club}
            etiqueta={
              marca.data?.logo_dark_asset_id ? 'Cambiarlo' : 'Subir la versión'
            }
            deshabilitado={!puedeEditar}
            alSubir={(asset) => ponerLogo.mutate({ oscuro: asset.id })}
          />
        </div>
      </div>

      <h2 className="seccion">Lo demás</h2>

      <label className="opcion">
        <input
          type="checkbox"
          checked={sinMarca}
          disabled={!puedeEditar}
          onChange={(e) => {
            setSinMarca(e.target.checked);
            setGuardado(false);
          }}
        />
        <span>
          <strong>Esconder que esto es Kiap</strong>
          <em>
            Quita el «con Kiap» de la app de las familias y de los correos. Tu
            club, y nada más.
          </em>
        </span>
      </label>

      <dl className="datos">
        {/*
          Solo si el club tiene uno. Decía «No tienes uno. Se pide a soporte.» a todos,
          y el dominio propio todavía no existe (`planes_premium.md` §6.1): era una
          promesa que nadie podía cumplir. 02-11-2026.
        */}
        {m.custom_domain && (
          <div>
            <dt>Dominio propio</dt>
            {/* De solo lectura: es único en la plataforma y se cambia desde soporte. */}
            <dd>{m.custom_domain}</dd>
          </div>
        )}
        <div>
          <dt>Última vez que se tocó</dt>
          <dd>{m.updated_at ? fechaYHora(m.updated_at) : 'Nunca'}</dd>
        </div>
      </dl>

      {problema && <PanelError problema={problema} />}

      {guardado && !guardar.isPending && (
        <div className="aviso aviso-calma" role="status">
          <strong>Guardado.</strong> El panel ya lleva tu color.
        </div>
      )}

      {puedeEditar && (
        <div className="acciones">
          <button
            type="submit"
            disabled={guardar.isPending || !cambiado || !valido}
          >
            {guardar.isPending ? 'Guardando…' : 'Guardar la marca'}
          </button>
          {/*
            Volver al verde de Kiap es una acción, no un campo vacío: nadie borra un
            hexadecimal a mano para eso.
          */}
          {m.brand_color !== null && (
            <button
              type="button"
              className="btn-plano"
              disabled={guardar.isPending}
              onClick={() => cambiar('')}
            >
              Volver al color de Kiap
            </button>
          )}
        </div>
      )}
    </form>
  );
}
